import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import t from './tokens';

// Embeds the Printess web-to-print editor (the same iframe + postMessage flow as
// the admin PoC at /admin/printess/test) for a personalized card/gift. The
// student's watermarked pose is passed in as a form field so the template can
// place it; "Add to Basket" returns a save token + thumbnail we put on the cart.
// Without a PRINTESS_SHOP_TOKEN the editor loads Printess's demo mode.
const EDITOR_ORIGIN = 'https://editor.printess.com';
const EDITOR_SRC = `${EDITOR_ORIGIN}/printess-editor/embed.html`;

const s = {
  page: {
    position: 'fixed', inset: 0, zIndex: 1100, background: '#111827', display: 'flex', flexDirection: 'column',
  },
  bar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem',
    padding: '0.6rem 1rem', background: t.surface, borderBottom: `1px solid ${t.border}`,
  },
  back: {
    background: 'none', border: 'none', color: t.brand, fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem',
  },
  title: { fontWeight: 700, color: t.ink },
  note: { color: t.muted, fontSize: '0.8rem' },
  frameWrap: { position: 'relative', flex: 1, background: '#1f2937' },
  frame: { width: '100%', height: '100%', border: 'none', display: 'block' },
  busy: {
    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', gap: '0.6rem',
  },
};

const PrintessEditor = ({
  token, product, photoUrl, photoField, adding, onBasket, onBack,
}) => {
  const frameRef = useRef(null);
  const [ready, setReady] = useState(false);

  // attach config -> Printess. The watermarked pose is passed as a form field so
  // the template can drop it into its photo placeholder (field name configurable).
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;
    const onLoad = () => {
      frame.contentWindow.postMessage({
        cmd: 'attach',
        properties: {
          token: token || '',
          templateName: product.template_id || '',
          basketId: `senior_${Date.now()}`,
          shopUserId: 'senior_shop',
          formFields: photoUrl ? [{ name: photoField, value: photoUrl }] : [],
        },
      }, '*');
      setReady(true);
    };
    frame.addEventListener('load', onLoad);
    return () => frame.removeEventListener('load', onLoad);
  }, [token, product.template_id, photoUrl, photoField]);

  // Editor -> us: "basket" = add to cart (save token + thumbnail); "back" = exit.
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== EDITOR_ORIGIN) return;
      const data = event.data || {};
      if (data.cmd === 'basket' && data.token) onBasket(data.token, data.thumbnailUrl);
      if (data.cmd === 'back') onBack();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onBasket, onBack]);

  return (
    <div style={s.page}>
      <div style={s.bar}>
        <button type="button" style={s.back} onClick={onBack}>← Back</button>
        <span style={s.title}>{`Personalize your ${product.name}`}</span>
        <span style={s.note}>{token ? '' : 'Demo mode'}</span>
      </div>
      <div style={s.frameWrap}>
        <iframe
          ref={frameRef}
          title="Printess editor"
          src={EDITOR_SRC}
          style={s.frame}
          allow="clipboard-read; clipboard-write"
        />
        {(!ready || adding) && (
          <div style={s.busy}>
            <span className="sg-lb-spinner" />
            <span>{adding ? 'Adding to cart…' : 'Loading editor…'}</span>
          </div>
        )}
      </div>
    </div>
  );
};

PrintessEditor.propTypes = {
  token: PropTypes.string,
  product: PropTypes.shape({
    id: PropTypes.number, name: PropTypes.string, template_id: PropTypes.string,
  }).isRequired,
  photoUrl: PropTypes.string,
  photoField: PropTypes.string,
  adding: PropTypes.bool,
  onBasket: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

PrintessEditor.defaultProps = {
  token: '', photoUrl: null, photoField: 'photo', adding: false,
};

export default PrintessEditor;
